import classNames from 'classnames';
import { compact } from 'lodash';
import moment, { Moment } from 'moment';
import { KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ActiveModifiers, DateAfter, DateBefore, DayPicker, Matcher } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { CalendarIcon } from '../../assets/icons';
import Button from '../../components/Button/Button';
import Divider from '../../components/Divider/Divider';
import TextField from '../../components/TextField/TextField';
import KeyCode from '../../types/enum/KeyCode';
import Position from '../../types/enum/Position';
import Size from '../../types/enum/Size';
import { isElementFocused } from '../../utilities/uiUtils';
import Icon from '../Icon/Icon';
import Popover from '../Popover/Popover';
import './DatePicker.scss';
import IDatePickerProps from './DatePickerProps';

const DatePicker = ({
  isOpen,
  onOpen,
  onClose,
  value,
  format,
  onChange,
  position,
  placeholder,
  disabledAfter,
  disabledBefore,
  closeOnSelect,
  error,
  todayButton,
  todayButtonLabel,
  showCalendarIcon,
  className,
}: IDatePickerProps) => {
  const [inputValue, setInputValue] = useState(formatDate(value));
  const [hasError, setHasError] = useState(false);

  const supportedFormats = useRef<string[]>([format, 'M/D/YY', 'M/D/YYYY', 'LL', 'L', 'll', 'l']);
  const firstRender = useRef<boolean>(false);
  const isDirty = useRef<boolean>(false);
  const input = useRef<HTMLInputElement>();
  const currentInputDate = useMemo(
    () => (isValidFormattedDate(inputValue) ? new Date(inputValue) : undefined),
    [inputValue]
  );

  const [selectedMonthDate, setSelectedMonthDate] = useState<Date | undefined>(currentInputDate);

  const isoValue = value?.toISOString() ?? null;
  useEffect(() => {
    const formatted = formatDate(value);
    const isValid = isValidDate(value);

    if (formatted !== inputValue && isValid && currentInputDate?.toDateString() !== value?.toDateString()) {
      setInputValue(formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoValue]);

  useEffect(() => {
    if (value === null) {
      setInputValue('');
      setHasError(false);
    } else {
      updateErrorState(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, error, inputValue]);

  useEffect(() => {
    if (!firstRender.current) {
      if (isOpen) {
        addListener();
      } else {
        removeListener();
      }
    } else {
      firstRender.current = true;
    }

    return () => removeListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function formatDate(date: Date | string | null): string {
    return date ? moment(date).format(format) : '';
  }
  function addListener() {
    document.addEventListener('click', handleClickOutside);
  }

  function removeListener() {
    document.removeEventListener('click', handleClickOutside);
  }

  function getDatePayload(date: Date | null) {
    const isValid = isValidDate(date);
    return {
      value: isValid ? date : null,
      inputValue: isValid ? formatDate(date) : input.current ? input.current.value : '',
    };
  }

  function handleChange(date: Date | null, considerFocus = true) {
    if (!considerFocus || (input.current && !isElementFocused(input.current))) {
      onChange(getDatePayload(date));
    }
    if (isDirty && isDirty.current) {
      updateErrorState(date);
    }
  }

  function handleInputChange(val: string) {
    const date = isValidFormattedDate(val) ? new Date(val) : null;
    handleChange(date);
    setInputText(val);
  }

  function handleInputClick(_val: string, _event: MouseEvent) {
    if (!isOpen) {
      onOpen();
    }
  }

  function handleDayClick(date: Date, activeModifiers: ActiveModifiers, _e: React.MouseEvent) {
    const disabled = !!activeModifiers.disabled;
    if (!disabled) {
      finishCalendarSelection(moment(date).startOf('day').toDate());
    }
  }

  function handleTodayPress() {
    const today = moment().startOf('day').toDate();
    finishCalendarSelection(today);
  }

  function onDatePickerClose(date: Date | undefined) {
    setSelectedMonthDate(date);
    onClose();
  }

  function finishCalendarSelection(date: Date) {
    handleChange(date, false);
    if (closeOnSelect) {
      onDatePickerClose(date);
      handleInputFocus();
    }
    setInputText(date);
  }

  function handleKeyDown(dateString: string, event: KeyboardEvent) {
    const keyCode = event.keyCode;
    const arrowKeyCodes = [KeyCode.ARROW_LEFT, KeyCode.ARROW_UP, KeyCode.ARROW_RIGHT, KeyCode.ARROW_DOWN];
    const closeKeyCodes = [KeyCode.TAB, KeyCode.ESCAPE];
    const openToggleKeyCodes = [KeyCode.ENTER];
    if (arrowKeyCodes.includes(keyCode)) {
      let date: Moment | Date | null = isValidFormattedDate(dateString) ? moment(new Date(dateString)) : null;

      // Left and right just change cursor position
      if (!isOpen && (keyCode === KeyCode.ARROW_DOWN || keyCode === KeyCode.ARROW_UP)) {
        onOpen();
        event.preventDefault();
      }

      if (date) {
        if (keyCode === KeyCode.ARROW_DOWN) {
          date = date.add(7, 'days');
        } else if (keyCode === KeyCode.ARROW_UP) {
          date = date.subtract(7, 'days');
        } else if (keyCode === KeyCode.ARROW_LEFT) {
          if (isOpen) {
            date = date.subtract(1, 'days');
          } else {
            return;
          }
        } else if (keyCode === KeyCode.ARROW_RIGHT) {
          if (isOpen) {
            date = date.add(1, 'days');
          } else {
            return;
          }
        }
        date = date.toDate(); // convert moment to date
      } else {
        date = new Date();
      }

      if (isDisabledDate(date)) {
        date = getNearestEnabledDate(date);
      }

      setInputText(formatDate(date));
      handleChange(date, false);
      selectInputText();
    } else if (closeKeyCodes.includes(keyCode)) {
      if (keyCode !== KeyCode.TAB) {
        event.preventDefault();
      }
      if (isOpen) {
        onDatePickerClose(currentInputDate);
      }
    } else if (openToggleKeyCodes.includes(keyCode)) {
      event.stopPropagation();
      event.preventDefault();
      if (isOpen) {
        onDatePickerClose(currentInputDate);
      } else {
        onOpen();
      }
    }
  }

  function handleInputFocus() {
    selectInputText();
  }

  function setInputText(val: Date | string | null) {
    let text;
    if (val instanceof Date) {
      text = formatDate(value);
    } else if (val === null) {
      text = '';
    } else {
      text = val;
    }

    setInputValue(text);
  }

  function getNearestEnabledDate(disabledDate: Date): Date | null {
    if (disabledAfter && disabledBefore) {
      if (disabledAfter < disabledBefore) {
        // eslint-disable-next-line no-console
        console.error('Prop error: disabledAfter must not be before disableBefore');
        return null;
      }
      return disabledDate.getTime() > disabledAfter.getTime() ? disabledAfter : disabledBefore;
    } else if (disabledAfter) {
      return disabledAfter;
    } else if (disabledBefore) {
      return disabledBefore;
    } else {
      return null;
    }
  }

  function selectInputText(delay = 0) {
    if (input.current) {
      setTimeout(() => {
        if (input.current) {
          input.current.select();
        }
      }, delay);
    }
  }

  function isValidDate(date: Date | null) {
    const valid = date instanceof Date && !Number.isNaN(date.getTime()) && !isDisabledDate(date);
    return valid;
  }

  function handleClickOutside(event: Event) {
    event.stopPropagation();
    if (
      !(event.target as HTMLElement).closest('.date-picker') &&
      !(event.target as HTMLElement).closest('.date-picker-shell')
    ) {
      onDatePickerClose(currentInputDate);
    }
  }

  function onInputBlur() {
    const date = isValidFormattedDate(inputValue) ? new Date(inputValue) : null;
    if (isValidDate(date)) {
      setInputText(formatDate(date));
    }

    handleChange(date, false);
  }

  function updateErrorState(date: Date | null) {
    let hasErr = false;
    const text = input.current ? input.current.value : '';
    isDirty.current = true;
    if (text !== '') {
      hasErr = !date || error || isDisabledDate(date) || !isValidFormattedDate(text);
    }

    if (hasError !== hasErr) {
      setHasError(hasErr);
    }
  }

  function isValidFormattedDate(dateString: string) {
    return supportedFormats.current.some((formatString) => {
      return moment(dateString, formatString, true).isValid();
    });
  }

  function isDisabledDate(date: Date) {
    let isDisabled = false;

    if (disabledBefore) {
      if (moment(date).isBefore(moment(disabledBefore).startOf('day'))) {
        isDisabled = true;
      }
    }
    if (!isDisabled && disabledAfter) {
      if (moment(date).isAfter(moment(disabledAfter).endOf('day'))) {
        isDisabled = true;
      }
    }
    return isDisabled;
  }

  function handleIconOnMouseDown(_event: MouseEvent) {
    if (isOpen) {
      onDatePickerClose(currentInputDate);
    } else {
      onOpen();
      if (input.current) {
        input.current.focus();
      }
    }
  }

  function getTrigger() {
    return input.current!;
  }

  function assignRef(element: HTMLInputElement) {
    input.current = element;
  }

  const beforeMatcher: DateBefore | null = disabledBefore ? { before: disabledBefore } : null;
  const afterMatcher: DateAfter | null = disabledAfter ? { after: disabledAfter } : null;

  const disabledMatcher: Matcher[] | undefined =
    beforeMatcher || afterMatcher ? compact([beforeMatcher, afterMatcher]) : undefined;

  const classes = classNames('date-picker', 'anim', className);

  return (
    <div className="date-picker-shell">
      <TextField
        placeholder={placeholder || format}
        value={inputValue}
        onClick={handleInputClick}
        onChange={handleInputChange}
        onBlur={onInputBlur}
        onFocus={handleInputFocus}
        error={hasError || error}
        onKeyDown={handleKeyDown}
        inputRef={assignRef}
      />
      {showCalendarIcon && (
        <Button type="icon" size={Size.SMALL} tabIndex={-1} onMouseDown={handleIconOnMouseDown}>
          <Icon svg={CalendarIcon} />
        </Button>
      )}
      <Popover
        isOpen={isOpen}
        transitionName="slide-open"
        getTargetNode={getTrigger}
        position={position}
        canFlip={false}
      >
        <div className={classes} tabIndex={0}>
          <DayPicker
            month={selectedMonthDate}
            selected={currentInputDate}
            onMonthChange={setSelectedMonthDate}
            disabled={disabledMatcher}
            mode="single"
            onDayClick={handleDayClick}
          />
          <Divider />
          {todayButton && (
            <div className="date-picker-footer">
              <Button type="link" onClick={handleTodayPress} disabled={isDisabledDate(new Date())}>
                {todayButtonLabel}
              </Button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
};

DatePicker.defaultProps = {
  format: 'M/D/YYYY',
  closeOnSelect: true,
  position: Position.BOTTOM_START,
  error: false,
  todayButton: true,
  todayButtonLabel: 'Today',
  showCalendarIcon: true,
};

export default DatePicker;
